import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Chart,
  LinearScale,
  PointElement,
  ScatterController,
  Tooltip,
  type ChartConfiguration,
  type Plugin,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import type { SectorBrandRow } from './api';
import { RISK_HEX, formatDecimal } from './palette';

/**
 * Matrice di posizionamento FameIntegrity × FameIndex (Level 3).
 *
 * Chart.js 4.4.1 e chartjs-plugin-annotation 3.0.1 sono le versioni esatte del
 * riferimento, installate come dipendenze invece che da CDN: in produzione il
 * browser degli utenti non deve contattare host esterni (CSP e DPA).
 *
 * Si registrano solo i componenti usati, invece di `Chart.register(...registerables)`:
 * il bundle non porta assi e controller che non servono.
 */
Chart.register(LinearScale, PointElement, ScatterController, Tooltip, annotationPlugin);

interface MatrixPoint {
  x: number;
  y: number;
  name: string;
  risk: number;
  current: boolean;
}

interface MatrixChartProps {
  brands: SectorBrandRow[];
  currentSlug: string;
}

export function MatrixChart({ brands, currentSlug }: MatrixChartProps): ReactNode {
  const canvas = useRef<HTMLCanvasElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const element = canvas.current;
    if (!element) return;

    const solidData: MatrixPoint[] = [];
    const nullData: MatrixPoint[] = [];
    const solidColors: string[] = [];
    const nullColors: string[] = [];
    const solidRadius: number[] = [];
    const nullRadius: number[] = [];

    for (const brand of brands) {
      const isCurrent = brand.slug === currentSlug;
      const radius = isCurrent ? 10 : 7;
      const risk = brand.fameRiskLevel;
      const integrity = brand.fameIntegrity ?? 0;

      if (brand.fameIndex !== null) {
        solidData.push({
          x: brand.fameIndex,
          y: integrity,
          name: brand.name,
          risk,
          current: isCurrent,
        });
        solidColors.push(RISK_HEX[risk] ?? '#A32D2D');
        solidRadius.push(radius);
      } else {
        // I brand con FameIndex N.D. non hanno una posizione sull'asse x:
        // si accostano al bordo sinistro con un jitter progressivo, così non
        // si sovrappongono fra loro (spec §6).
        const jitter = nullData.length * 0.18;
        nullData.push({
          x: 1.55 + jitter,
          y: integrity,
          name: brand.name,
          risk,
          current: isCurrent,
        });
        nullColors.push(RISK_HEX[risk] ?? '#888');
        nullRadius.push(radius);
      }
    }

    // [YBD] Il riferimento legge `prefers-color-scheme` e in dark mode di
    // sistema passa a tick, griglia ed etichette bianchi: ma il fondo della
    // dashboard resta chiaro (nessuna @media dark nel CSS), quindi le scritte
    // della matrice diventavano illeggibili. Tema chiaro fisso, come la pagina.
    const gridCol = 'rgba(0,0,0,0.06)';
    const lineCol = 'rgba(0,0,0,0.12)';
    const tickCol = 'rgba(0,0,0,0.55)';
    const quadrantLabelCol = 'rgba(0,0,0,0.55)';

    /** Etichette dei brand accanto ai punti, con anti-collisione. */
    const labelPlugin: Plugin<'scatter'> = {
      id: 'brandLabels',
      // `afterDatasetsDraw` e non `afterDraw`: il tooltip di Chart.js si disegna
      // in `afterDraw`, quindi da lì le etichette finivano **sopra** il suo
      // riquadro e le due scritte si mescolavano (feedback 23/10, punto 7).
      // Qui le etichette restano sopra i punti e sotto il tooltip.
      afterDatasetsDraw(chart) {
        const ctx = chart.ctx;
        const fontSize = 11;
        ctx.font = `500 ${fontSize}px sans-serif`;

        const labels: { name: string; px: number; py: number; w: number }[] = [];
        chart.data.datasets.forEach((ds, di) => {
          (ds.data as unknown as MatrixPoint[]).forEach((p, i) => {
            const meta = chart.getDatasetMeta(di).data[i];
            if (meta)
              labels.push({
                name: p.name,
                px: meta.x,
                py: meta.y,
                w: ctx.measureText(p.name).width,
              });
          });
        });

        labels.sort((a, b) => a.py - b.py);

        const placed: { lx: number; ly: number; w: number; side: string }[] = [];
        const minGap = fontSize + 4;

        for (const lbl of labels) {
          let lx = lbl.px + 13;
          let ly = lbl.py + 4;
          let side = 'right';

          for (let attempt = 0; attempt < 8; attempt++) {
            let collision = false;
            for (const p of placed) {
              const overlapX = side === p.side && Math.abs(lx - p.lx) < Math.max(lbl.w, p.w) + 8;
              if (overlapX && Math.abs(ly - p.ly) < minGap) {
                collision = true;
                break;
              }
            }
            if (!collision) break;

            if (attempt === 0) ly = lbl.py - fontSize;
            else if (attempt === 1) {
              side = 'left';
              lx = lbl.px - lbl.w - 13;
              ly = lbl.py + 4;
            } else if (attempt === 2) {
              ly = lbl.py + fontSize + 8;
              side = 'right';
              lx = lbl.px + 13;
            } else if (attempt === 3) {
              ly = lbl.py - fontSize * 2;
              lx = lbl.px + 13;
            } else {
              ly = lbl.py + (attempt - 1) * minGap;
              lx = lbl.px + 13;
            }
          }

          placed.push({ lx, ly, w: lbl.w, side });
          ctx.fillStyle = 'rgba(0,0,0,0.75)';
          ctx.textAlign = side === 'left' ? 'right' : 'left';
          ctx.fillText(lbl.name, lx, ly);
        }
      },
    };

    const config: ChartConfiguration<'scatter'> = {
      type: 'scatter',
      data: {
        datasets: [
          {
            label: 'Brands',
            data: solidData,
            backgroundColor: solidColors,
            borderColor: solidData.map((d, i) =>
              d.current ? '#0A1B38' : (solidColors[i] ?? '#A32D2D'),
            ),
            borderWidth: solidData.map((d) => (d.current ? 3 : 1)),
            pointRadius: solidRadius,
            pointHoverRadius: solidRadius.map((r) => r + 2),
          },
          {
            // Non visibile (la legenda di Chart.js è spenta), ma tradotta
            // comunque: è il titolo che il tooltip userebbe se la si accendesse.
            label: t('integrity.nd'),
            data: nullData,
            backgroundColor: 'transparent',
            borderColor: nullColors,
            borderWidth: nullData.map((d) => (d.current ? 3 : 2)),
            pointRadius: nullRadius,
            pointStyle: 'circle',
            pointHoverRadius: nullRadius.map((r) => r + 2),
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 25, right: 15, bottom: 5, left: 5 } },
        scales: {
          x: {
            min: 1.4,
            max: 4.6,
            grid: { color: gridCol },
            ticks: { color: tickCol, font: { size: 12 }, stepSize: 0.5 },
          },
          y: {
            min: 0,
            max: 100,
            grid: { color: gridCol },
            ticks: { color: tickCol, font: { size: 12 }, callback: (v) => `${String(v)}%` },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            // Fondo pieno: il default di Chart.js è nero all'80%, e sotto
            // traspariva l'etichetta del brand (feedback 23/10, punto 7). È
            // `--navy` scritto per esteso, come gli altri colori di questo file:
            // il grafico disegna su canvas e non legge le custom properties.
            backgroundColor: '#142E4F',
            callbacks: {
              label(ctx) {
                const p = ctx.raw as MatrixPoint;
                // I punti del secondo dataset sono i N.D.: la loro x è una
                // posizione grafica, non un valore da mostrare.
                const isNd = ctx.datasetIndex === 1;
                const indice = isNd ? t('integrity.nd') : formatDecimal(p.x, 2);
                // Soli due valori, con i nomi degli indici (feedback 23/10,
                // punto 3): il nome del brand è già scritto accanto al punto.
                // "FameIntegrity" e "FameIndex" sono nomi di prodotto, identici
                // nelle due lingue, quindi non passano dalla localizzazione —
                // che è anche il motivo per cui non sono mai state tradotte le
                // sigle `FI`/`FIdx` del riferimento.
                return `FameIntegrity ${p.y}% \u00b7 FameIndex ${indice}`;
              },
            },
          },
          annotation: {
            annotations: {
              vLine: {
                type: 'line',
                xMin: 3.0,
                xMax: 3.0,
                borderColor: lineCol,
                borderWidth: 1.5,
                borderDash: [6, 4],
              },
              hLine: {
                type: 'line',
                yMin: 50,
                yMax: 50,
                borderColor: lineCol,
                borderWidth: 1.5,
                borderDash: [6, 4],
              },
              boxTR: {
                type: 'box',
                xMin: 3.0,
                xMax: 4.6,
                yMin: 50,
                yMax: 100,
                backgroundColor: 'rgba(42,157,143,0.06)',
                borderWidth: 0,
              },
              boxTL: {
                type: 'box',
                xMin: 1.4,
                xMax: 3.0,
                yMin: 50,
                yMax: 100,
                backgroundColor: 'rgba(74,158,197,0.06)',
                borderWidth: 0,
              },
              boxBR: {
                type: 'box',
                xMin: 3.0,
                xMax: 4.6,
                yMin: 0,
                yMax: 50,
                backgroundColor: 'rgba(239,159,39,0.06)',
                borderWidth: 0,
              },
              boxBL: {
                type: 'box',
                xMin: 1.4,
                xMax: 3.0,
                yMin: 0,
                yMax: 50,
                backgroundColor: 'rgba(163,45,45,0.06)',
                borderWidth: 0,
              },
              // Il v40 ha tolto la fascia `zoneSafe` (tutta la banda con
              // FameIntegrity >= 50) e ha ristretto la zona certificabile al
              // solo quadrante alto-destro: non basta più l'integrità, serve
              // anche un FameIndex sopra 3.0.
              zoneCert: {
                type: 'box',
                xMin: 3.0,
                xMax: 4.6,
                yMin: 80,
                yMax: 100,
                backgroundColor: 'rgba(42,157,143,0.12)',
                borderColor: 'rgba(42,157,143,0.40)',
                borderWidth: 1.5,
                borderDash: [4, 3],
              },
              lblSafe: {
                type: 'label',
                xValue: 4.28,
                yValue: 53,
                content: [t('intelligence.zoneSafe')],
                color: 'rgba(0,0,0,0.30)',
                font: { size: 10, style: 'italic' },
              },
              lblCert: {
                type: 'label',
                xValue: 4.3,
                yValue: 83,
                content: [t('intelligence.zoneFamecert')],
                color: 'rgba(42,157,143,0.55)',
                font: { size: 10, style: 'italic' },
              },
              lblTR: {
                type: 'label',
                xValue: 3.8,
                yValue: 96,
                content: [t('quadrant.areaSicura')],
                color: quadrantLabelCol,
                font: { size: 12, style: 'italic', weight: 500 },
              },
              lblTL: {
                type: 'label',
                xValue: 2.2,
                yValue: 96,
                content: [t('quadrant.rischioOperativo')],
                color: quadrantLabelCol,
                font: { size: 12, style: 'italic', weight: 500 },
              },
              lblBR: {
                type: 'label',
                xValue: 3.8,
                yValue: 4,
                content: [t('quadrant.rischioRegolatorio')],
                color: quadrantLabelCol,
                font: { size: 12, style: 'italic', weight: 500 },
              },
              lblBL: {
                type: 'label',
                xValue: 2.2,
                yValue: 4,
                content: [t('quadrant.rischioCritico')],
                color: quadrantLabelCol,
                font: { size: 12, style: 'italic', weight: 500 },
              },
            },
          },
        },
      },
      plugins: [labelPlugin],
    };

    const chart = new Chart(element, config);
    return () => chart.destroy();
  }, [brands, currentSlug, t]);

  return <canvas ref={canvas} />;
}
